import React from 'react';
import { Button } from '@uhg-abyss/web/ui/Button';
import { IconSymbol } from '@uhg-abyss/web/ui/IconSymbol';
import { ModalDialog } from '@uhg-abyss/web/ui/ModalDialog';
import { Text } from '@uhg-abyss/web/ui/Text';
import { styled } from '@uhg-abyss/web/tools/styled';
import type { SharedDialogAction, SharedDialogProps } from './SharedDialog.types';

// BODY HEADING RULE: Never render the dialog title again inside children.
// The title is rendered exactly once via the ModalDialog header prop.

const ABYSS_SIZE: Record<'sm' | 'md' | 'lg', string> = {
  sm: 'sm',
  md: 'md',
  lg: '48rem',
};

const MODE_ICON = {
  error: 'error',
  confirm: 'warning',
  app: null,
} as const;

const MODE_ICON_COLOR = {
  error: '#c0392b',
  confirm: '#b7791f',
  app: 'currentColor',
} as const;

// Header background and border per mode
const MODE_HEADER_BG = {
  error: '#fef2f2',
  confirm: '#fefce8',
  app: '#eef4fb',
} as const;

const MODE_HEADER_BORDER = {
  error: '#fca5a5',
  confirm: '#fde68a',
  app: '#bfdbfe',
} as const;

const HeaderRow = styled('div', {
  display: 'flex',
  alignItems: 'center',
  gap: '0.5rem',
});

const FooterRow = styled('div', {
  display: 'flex',
  gap: '0.75rem',
  justifyContent: 'flex-end',
});

const BodyStack = styled('div', {
  display: 'grid',
  gap: '0.75rem',
});

// TODO: When Abyss Button gains a native intent/danger prop, wire SharedDialogAction.intent here.
const DangerButton = styled(Button, {
  '&.abyss-button-root': {
    backgroundColor: '#c0392b',
    borderColor: '#c0392b',
    color: '#fff',
    '&:hover': { backgroundColor: '#a93226', borderColor: '#a93226' },
    '&:focus-visible': { outlineColor: '#c0392b' },
  },
});

function renderActionButton(
  action: SharedDialogAction,
  defaultVariant: 'filled' | 'outline' | 'text',
  ref?: React.RefObject<HTMLButtonElement | null>,
) {
  const variant = action.variant ?? defaultVariant;
  const sharedProps = {
    ref,
    variant,
    isDisabled: action.disabled,
    isLoading: action.loading,
    ariaLoadingLabel: action.ariaLoadingLabel ?? `${action.label} in progress`,
    onClick: action.onClick,
  } as const;

  if (action.intent === 'danger' && variant === 'filled') {
    return <DangerButton {...sharedProps}>{action.label}</DangerButton>;
  }

  return <Button {...sharedProps}>{action.label}</Button>;
}

export const SharedDialog = ({
  isOpen,
  onClose,
  mode = 'app',
  title,
  description,
  size = 'md',
  primaryAction,
  secondaryAction,
  tertiaryAction,
  children,
  ariaLabel,
  initialFocus = 'none',
  dismissible,
}: SharedDialogProps) => {
  const isErrorMode = mode === 'error';
  const isConfirmMode = mode === 'confirm';
  const effectiveDismissible = dismissible ?? !isErrorMode;

  const primaryRef = React.useRef<HTMLButtonElement | null>(null);
  const secondaryRef = React.useRef<HTMLButtonElement | null>(null);

  React.useEffect(() => {
    if (!isOpen) return;
    const focusRef =
      initialFocus === 'primary' ? primaryRef
      : initialFocus === 'secondary' ? secondaryRef
      : initialFocus === 'none' && isConfirmMode ? secondaryRef
      : null;
    focusRef?.current?.focus();
  }, [isOpen, initialFocus, isConfirmMode]);

  const iconName = MODE_ICON[mode];
  const iconColor = MODE_ICON_COLOR[mode];
  const headerBg = MODE_HEADER_BG[mode];
  const headerBorder = MODE_HEADER_BORDER[mode];

  const header = (
    <HeaderRow>
      {iconName && (
        <IconSymbol
          icon={iconName}
          style={{ color: iconColor, fontSize: '1.25rem', flexShrink: 0 }}
        />
      )}
      <Text style={{ margin: 0, fontSize: '1.1rem', fontWeight: 600 }}>{title}</Text>
    </HeaderRow>
  );

  const hasFooter = primaryAction || secondaryAction || tertiaryAction;
  const footer = hasFooter ? (
    <FooterRow>
      {tertiaryAction && renderActionButton(tertiaryAction, 'text')}
      {secondaryAction && renderActionButton(secondaryAction, 'outline', secondaryRef)}
      {primaryAction && renderActionButton(primaryAction, 'filled', primaryRef)}
    </FooterRow>
  ) : undefined;

  return (
    <ModalDialog
      ariaLabel={ariaLabel ?? (typeof title === 'string' ? title : 'Dialog')}
      isOpen={isOpen}
      onClose={onClose}
      size={ABYSS_SIZE[size]}
      closeOnClickOutside={effectiveDismissible}
      hideClose={!effectiveDismissible}
      header={header}
      footer={footer}
      css={{
        'abyss-modal-base-header-container': {
          backgroundColor: headerBg,
          borderBottom: `1px solid ${headerBorder}`,
          borderRadius: '8px 8px 0 0',
          paddingBottom: '1rem',
        },
        'abyss-modal-base-body': {
          paddingTop: '1.25rem',
          paddingBottom: '1.25rem',
        },
        'abyss-modal-base-footer': {
          backgroundColor: '#f8fafc',
          borderTop: '1px solid #e2e8f0',
          borderRadius: '0 0 8px 8px',
          paddingTop: '1rem',
        },
      }}
    >
      <BodyStack>
        {description && <Text style={{ margin: 0 }}>{description}</Text>}
        {children}
      </BodyStack>
    </ModalDialog>
  );
};
